import * as React from 'react'
import { Tabs } from 'radix-ui'

import { cn } from '../../lib/utils'

export type TabNavVariant = 'default' | 'pills' | 'links' | 'toggle'

export type TabNavSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface TabItem<T extends string = string> {
  key: T
  label: string
  icon?: React.ReactNode
  disabled?: boolean
}

interface TabNavProps<T extends string = string> {
  tabs: TabItem<T>[]
  activeTab: T
  onChange: (tab: T) => void
  className?: string
  /** When true, tabs expand to fill the container width (default: tabs hug their content). */
  fullWidth?: boolean
  variant?: TabNavVariant
  /** Control padding, height, and type scale of tab triggers */
  size?: TabNavSize
  /** Custom render function for tab content. Receives the tab item and returns ReactNode. */
  renderTab?: (tab: TabItem<T>) => React.ReactNode
}

const TAB_NAV_SIZE: Record<
  TabNavSize,
  { icon: string; default: string; pills: string; links: string; toggle: string; soon: string; listGapPills: string }
> = {
  xs: {
    icon: 'tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-3',
    default: 'tw:h-7 tw:px-2 tw:text-xs tw:gap-1',
    pills: 'tw:min-h-7 tw:px-2.5 tw:py-1 tw:text-xs tw:gap-1',
    links: 'tw:min-h-7 tw:px-0.5 tw:pb-0.5 tw:pt-0.5 tw:text-xs tw:gap-1 tw:border-b-2',
    toggle: 'tw:min-h-7 tw:px-3 tw:py-1 tw:text-xs tw:gap-1',
    soon: 'tw:px-1 tw:py-0.5 tw:text-[0.6rem]',
    listGapPills: 'tw:gap-1.5',
  },
  sm: {
    icon: 'tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-3.5',
    default: 'tw:h-8 tw:px-2.5 tw:text-xs tw:gap-1.5',
    pills: 'tw:min-h-8 tw:px-3 tw:py-1.5 tw:text-xs tw:gap-1.5',
    links: 'tw:min-h-8 tw:px-0.5 tw:pb-0.5 tw:pt-0.5 tw:text-xs tw:gap-1.5 tw:border-b-2',
    toggle: 'tw:min-h-8 tw:px-3.5 tw:py-1.5 tw:text-xs tw:gap-1.5',
    soon: 'tw:px-1 tw:py-0.5 tw:text-[0.62rem]',
    listGapPills: 'tw:gap-1.5',
  },
  md: {
    icon: 'tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4',
    default: 'tw:h-10 tw:px-3 tw:text-sm tw:gap-1.5',
    pills: 'tw:min-h-10 tw:px-4 tw:py-2 tw:text-sm tw:gap-1.5',
    links: 'tw:min-h-10 tw:px-1 tw:pb-1 tw:pt-1 tw:text-sm tw:gap-1.5 tw:border-b-4',
    toggle: 'tw:min-h-10 tw:px-4 tw:py-2 tw:text-sm tw:gap-1.5',
    soon: 'tw:px-1.5 tw:py-0.5 tw:text-[0.65rem]',
    listGapPills: 'tw:gap-2',
  },
  lg: {
    icon: 'tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-5',
    default: 'tw:h-11 tw:px-4 tw:text-base tw:gap-2',
    pills: 'tw:min-h-11 tw:px-5 tw:py-2.5 tw:text-base tw:gap-2',
    links: 'tw:min-h-11 tw:px-1.5 tw:pb-1.5 tw:pt-1.5 tw:text-base tw:gap-2 tw:border-b-4',
    toggle: 'tw:min-h-11 tw:px-5 tw:py-2.5 tw:text-base tw:gap-2',
    soon: 'tw:px-2 tw:py-0.5 tw:text-xs',
    listGapPills: 'tw:gap-2.5',
  },
  xl: {
    icon: 'tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-6',
    default: 'tw:h-12 tw:px-5 tw:text-lg tw:gap-2',
    pills: 'tw:min-h-12 tw:px-6 tw:py-3 tw:text-lg tw:gap-2',
    // Slightly thicker active underline to match scale
    links: 'tw:min-h-12 tw:px-2 tw:pb-2 tw:pt-2 tw:text-lg tw:gap-2 tw:border-b-[5px]',
    toggle: 'tw:min-h-12 tw:px-6 tw:py-3 tw:text-lg tw:gap-2',
    soon: 'tw:px-2 tw:py-0.5 tw:text-sm',
    listGapPills: 'tw:gap-3',
  },
}

// `appearance-none` + `border-0` avoid Bootstrap / UA default button chrome.
const defaultTriggerClassName = cn(
  'tw:appearance-none tw:m-0 tw:inline-flex tw:min-w-0 tw:select-none tw:items-center tw:justify-center',
  'tw:border-0 tw:border-b-3 tw:border-transparent tw:bg-background tw:leading-none tw:text-muted-foreground',
  'tw:shadow-none tw:transition-colors',
  'tw:hover:text-foreground tw:data-[state=active]:text-primary',
  'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
  'tw:disabled:pointer-events-none tw:disabled:opacity-50',
  'tw:data-[state=active]:text-blue-500',
  'tw:data-[state=active]:border-b-3 tw:data-[state=active]:border-blue-500',
)

// Outlined capsules: grey border + charcoal when idle; light-blue border, wash, blue text when selected;
// disabled pills stay muted with a faint border (see Soon badge in render).
const pillsTriggerClassName = cn(
  'tw:appearance-none tw:m-0 tw:inline-flex tw:min-w-0 tw:shrink-0 tw:select-none tw:items-center tw:justify-center',
  'tw:rounded-full tw:border tw:border-solid tw:border-border tw:bg-background tw:leading-none',
  'tw:font-medium tw:text-foreground tw:[&_svg]:text-current tw:shadow-none',
  'tw:transition-colors',
  'tw:hover:enabled:border-neutral-400/80',
  'tw:data-[state=active]:border-blue-400 tw:data-[state=active]:bg-blue-50 tw:data-[state=active]:font-semibold tw:data-[state=active]:text-blue-600',
  'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2',
  'tw:disabled:pointer-events-none tw:disabled:border-border/40 tw:disabled:bg-transparent tw:disabled:font-medium',
  'tw:disabled:text-muted-foreground/80 tw:disabled:data-[state=active]:border-border/40 tw:disabled:data-[state=active]:bg-transparent',
  'tw:disabled:data-[state=active]:font-medium tw:disabled:data-[state=active]:text-muted-foreground/80',
)

// Segmented toggle: muted container, rounded active item as a clean white
// segment with blue text. No drop-shadow or ring — relies on the background
// contrast against the muted container for definition, which avoids the
// shadow-clipping artefact that appears when the container's padding is
// smaller than the shadow's spread.
const toggleTriggerClassName = cn(
  'tw:appearance-none tw:m-0.5 tw:inline-flex tw:min-w-0 tw:shrink-0 tw:select-none tw:items-center tw:justify-center',
  'tw:rounded-md tw:border-0 tw:bg-transparent tw:leading-none',
  'tw:font-medium tw:text-muted-foreground tw:shadow-none tw:transition-colors',
  'tw:hover:text-foreground',
  'tw:data-[state=active]:bg-background tw:data-[state=active]:text-blue-600 tw:data-[state=active]:shadow-sm',
  'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring',
  'tw:disabled:pointer-events-none tw:disabled:opacity-50',
)

const linksTriggerClassName = cn(
  'tw:appearance-none tw:m-0 tw:inline-flex tw:min-w-0 tw:shrink-0 tw:select-none tw:items-center tw:justify-center',
  'tw:-mb-px tw:border-0 tw:border-transparent tw:bg-transparent tw:leading-none',
  'tw:text-muted-foreground tw:shadow-none tw:transition-colors',
  'tw:hover:text-foreground',
  'tw:data-[state=active]:border-green-600 tw:data-[state=active]:font-medium tw:data-[state=active]:text-foreground',
  'tw:focus-visible:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring tw:focus-visible:ring-offset-2',
  'tw:disabled:pointer-events-none tw:disabled:opacity-50',
)

function TabNav<T extends string = string>({
  tabs,
  activeTab,
  onChange,
  className,
  fullWidth,
  variant = 'default',
  size = 'md',
  renderTab,
}: TabNavProps<T>) {
  const s = TAB_NAV_SIZE[size]

  const triggerBase =
    variant === 'pills'
      ? cn(pillsTriggerClassName, s.pills, s.icon)
      : variant === 'toggle'
        ? cn(toggleTriggerClassName, s.toggle, s.icon)
        : variant === 'links'
          ? cn(linksTriggerClassName, s.links, s.icon)
          : cn(defaultTriggerClassName, s.default, s.icon)

  const triggerClassName = fullWidth ? cn(triggerBase, 'tw:shrink-0') : triggerBase

  const rootClassName = cn(
    'tw:flex tw:flex-col',
    variant === 'toggle'
      ? 'tw:rounded-lg tw:bg-muted/50 tw:p-1'
      : ['pills', 'links'].includes(variant) ? 'tw:bg-transparent' : 'tw:rounded-md tw:bg-background tw:pt-1 tw:shadow-md/10',
    !fullWidth && 'tw:w-fit',
    className,
  )

  const listClassName = cn(
    'tw:flex tw:min-h-0 tw:shrink-0 tw:overflow-x-auto tw:overflow-y-hidden tw:whitespace-nowrap',
    variant === 'pills'
      ? cn('tw:items-center tw:bg-transparent', s.listGapPills)
      : variant === 'toggle'
        ? 'tw:items-center tw:gap-1 tw:bg-transparent'
        : variant === 'links'
          ? 'tw:gap-6 tw:border-b tw:border-border tw:bg-transparent'
          : 'tw:border-x tw:border-border tw:bg-background',
  )

  return (
    <Tabs.Root value={activeTab} onValueChange={(value) => onChange(value as T)} className={rootClassName}>
      <Tabs.List className={listClassName}>
        {tabs.map((tab) => (
          <Tabs.Trigger
            key={tab.key}
            value={tab.key}
            disabled={tab.disabled}
            className={triggerClassName}
          >
            {renderTab ? renderTab(tab) : <>{tab.icon}{tab.label}</>}
            {tab.disabled && (
              <span
                className={cn(
                  'tw:inline-flex tw:items-center tw:font-medium tw:leading-none',
                  s.soon,
                  variant === 'pills'
                    ? 'tw:rounded-full tw:border tw:border-border/50 tw:bg-muted/60 tw:text-muted-foreground/90'
                    : 'tw:rounded tw:bg-muted tw:text-muted-foreground',
                )}
              >
                Soon
              </span>
            )}
          </Tabs.Trigger>
        ))}
      </Tabs.List>
    </Tabs.Root>
  )
}

export { TabNav }
